import {
  type UseQueryResult,
  type UseMutationResult,
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { apiInstance } from '../services/service-trips-api/service-trips-api';
import { toAbsenceId } from '../services/service-trips-api/types';
import type { AbsenceRequest, AbsenceResponse, AbsenceSearchRequest, AbsenceSearchResponse } from '../services/service-trips-api/types';
import { QueryKeys } from './types';

// ─── Query ───────────────────────────────────────────────────────────────────

export const useAbsenceSearchQuery = (
  request: AbsenceSearchRequest,
  enabled = true,
): UseQueryResult<AbsenceSearchResponse, Error> => {
  return useQuery({
    queryKey: [QueryKeys.SearchAbsences, request],
    queryFn: (): Promise<AbsenceSearchResponse> => apiInstance.searchAbsences(request),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
};

// ─── Mutations ───────────────────────────────────────────────────────────────

export const useCreateAbsenceMutation = (): UseMutationResult<
  AbsenceResponse,
  Error,
  AbsenceRequest
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AbsenceRequest): Promise<AbsenceResponse> =>
      apiInstance.createAbsence(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKeys.ListAbsences] });
      void queryClient.invalidateQueries({ queryKey: [QueryKeys.SearchAbsences] });
    },
  });
};

export const useUpdateAbsenceMutation = (): UseMutationResult<
  AbsenceResponse,
  Error,
  { absenceId: string; payload: AbsenceRequest }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      absenceId,
      payload,
    }: {
      absenceId: string;
      payload: AbsenceRequest;
    }): Promise<AbsenceResponse> => apiInstance.updateAbsence(toAbsenceId(absenceId), payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKeys.ListAbsences] });
      void queryClient.invalidateQueries({ queryKey: [QueryKeys.SearchAbsences] });
    },
  });
};

export const useCancelAbsenceMutation = (): UseMutationResult<
  void,
  Error,
  string
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (absenceId: string): Promise<void> =>
      apiInstance.cancelAbsence(toAbsenceId(absenceId)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKeys.ListAbsences] });
      void queryClient.invalidateQueries({ queryKey: [QueryKeys.SearchAbsences] });
    },
  });
};
